/**
 * RTK Query API slice for odl_app REST resources.
 *
 * The single entitiesApi instance that all odl_app resource modules inject
 * their endpoints into. Provides the shared baseQuery (axiosBaseQuery),
 * tag registry, and the createResourceEndpoints factory for uniform
 * list / retrieve / create / patch / delete + getGridSpec coverage.
 *
 * Resource modules (cameras.js, commands.js, …) call injectEndpoints on
 * this export. The store mounts entitiesApi.reducer at the 'entities' path
 * and includes entitiesApi.middleware for cache management.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from './baseQuery';

export const entitiesApi = createApi({
  reducerPath: 'entitiesApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'Camera',
    'Command',
    'Crew',
    'CrewExtraPay',
    'Customer',
    'JobService',
    'JobServicePrice',
    'OrderSale',
    'PrepaidCoverage',
    'Product',
    'Radio',
    'Rma',
    'SiteGroup',
    'Association',
    'Site',
    'Ticket',
    'User',
    'GridSpec',
    'Ref',
  ],
  // Generic, resource-agnostic endpoints live here — the runtime-resolved
  // EntityGrid and useResolveRef use them because they don't know the resource
  // at import time. Per-resource typed endpoints are injected by the modules.
  endpoints: build => ({
    // Resolve any { type, id } reference: caller passes the prebuilt path.
    retrieveEntity: build.query({
      query: ({ path }) => ({ url: path }),
      transformResponse: b => b.data,
      providesTags: (result, error, { path }) => [{ type: 'Ref', id: path }],
    }),
    // Grid descriptor for a runtime-resolved resource path.
    getGridSpecFor: build.query({
      query: ({ path, params }) => ({ url: `${path}/grid-spec/`, params }),
      transformResponse: b => b.data,
      keepUnusedDataFor: 3600,
      providesTags: (result, error, { path }) => [{ type: 'GridSpec', id: path }],
    }),
  }),
});

/**
 * Factory that injects a standard set of CRUD + grid-spec endpoints for one
 * resource collection into entitiesApi.
 *
 * The factory covers:
 *   list       GET  basePath/            — paginated list (grid query)
 *   retrieve   GET  basePath/{id}/       — single resource
 *   create     POST basePath/            — create resource
 *   patch      PATCH basePath/{id}/      — partial update
 *   remove     DELETE basePath/{id}/     — delete
 *   dispatch   POST basePath/            — action dispatch { commands:[…] }
 *   getGridSpec GET basePath/grid-spec/  — backend column descriptor
 *
 * Only the endpoints the caller asks for are registered; pass the names you
 * need in the `include` set (defaults to all).
 *
 * @param {string} name      Pascal-case tag name, e.g. 'Camera'
 * @param {string} basePath  URL path relative to apiClient baseURL,
 *                           e.g. 'sites/{siteId}/cameras' (no trailing slash)
 * @param {{ include?: Set<string> }} [opts]
 * @returns {object} Object with the injected api + named endpoint hooks
 *
 * @example
 *   export const { api: camerasApi, useListCamerasQuery } =
 *     createResourceEndpoints('Camera', 'sites/{siteId}/cameras');
 */
export function createResourceEndpoints(name, basePath, { include } = {}) {
  const all = new Set(['list', 'retrieve', 'create', 'patch', 'remove', 'dispatch', 'getGridSpec']);
  const active = include ?? all;

  const injected = entitiesApi.injectEndpoints({
    endpoints: build => {
      const defs = {};

      if (active.has('list')) {
        defs[`list${name}s`] = build.query({
          query: ({ path, grid } = {}) => ({
            url: path ?? `${basePath}/`,
            grid,
          }),
          transformResponse: b => ({ items: b.data, meta: b.meta }),
          providesTags: [{ type: name, id: 'LIST' }],
        });
      }

      if (active.has('retrieve')) {
        defs[`retrieve${name}`] = build.query({
          query: ({ path, id } = {}) => ({
            url: path ?? `${basePath}/${id}/`,
          }),
          transformResponse: b => b.data,
          providesTags: (result, error, { id }) => [{ type: name, id }],
        });
      }

      if (active.has('create')) {
        defs[`create${name}`] = build.mutation({
          query: ({ path, body } = {}) => ({
            url: path ?? `${basePath}/`,
            method: 'post',
            data: body,
          }),
          transformResponse: b => b.data,
          invalidatesTags: [{ type: name, id: 'LIST' }],
        });
      }

      if (active.has('patch')) {
        defs[`patch${name}`] = build.mutation({
          query: ({ path, id, body } = {}) => ({
            url: path ?? `${basePath}/${id}/`,
            method: 'patch',
            data: body,
          }),
          transformResponse: b => b.data,
          invalidatesTags: (result, error, { id }) => [
            { type: name, id: 'LIST' },
            { type: name, id },
          ],
        });
      }

      if (active.has('remove')) {
        defs[`remove${name}`] = build.mutation({
          query: ({ path, id } = {}) => ({
            url: path ?? `${basePath}/${id}/`,
            method: 'delete',
          }),
          invalidatesTags: (result, error, { id }) => [
            { type: name, id: 'LIST' },
            { type: name, id },
          ],
        });
      }

      if (active.has('dispatch')) {
        // Action dispatch: POST { commands: [{ key, params }] }
        // Returns the per-entry results array from the 200/207 envelope.
        defs[`dispatch${name}Commands`] = build.mutation({
          query: ({ path, commands } = {}) => ({
            url: path ?? `${basePath}/`,
            method: 'post',
            data: { commands },
          }),
          transformResponse: b => b.data,
        });
      }

      if (active.has('getGridSpec')) {
        defs[`get${name}GridSpec`] = build.query({
          query: ({ path } = {}) => ({
            url: path ?? `${basePath}/grid-spec/`,
          }),
          transformResponse: b => b.data,
          // Grid specs are stable per resource; cache indefinitely by default.
          keepUnusedDataFor: 3600,
          providesTags: [{ type: 'GridSpec', id: name }],
        });
      }

      return defs;
    },
  });

  return injected;
}
